import config from 'app/config/config'
import { client } from 'app/shared/api/client'
import { Dispatch } from 'redux'
import {
  getLendersPositionsByFacility,
  editLendersPosition,
  changeLendersForFacility,
  getLendersPositionsByLoan,
  startLoadingFacilityLendersPositions,
  stopLoadingFacilityLendersPositions,
  startLoadingLoanLendersPositions,
  stopLoadingLoanLendersPositions,
  changeLendersForLoan,
  getLendersPositions,
  getMultipleLendersPositionsByFacility,
} from './actions'
import {
  ChangeLender,
  LendersPosition,
} from 'app/models/lenders-position-params'
import { StableKey } from 'app/models/common-types'
import { getBusinessDateFromState } from '../business-date/thunks'
import { stringToDate } from 'app/utils/util-functions'
import { coalesceThunkByKey } from 'app/state/utils/coalesce-thunk'
import { FacilityParams } from 'app/models/facility-params'

export const getAllPositions = async () => {
  const apiServerUrl = config.backendUrl
  return await client
    .get(`${apiServerUrl}/api/lenders-positions`)
    .then(response => {
      return response.data
    })
    .catch(e => {
      console.log(e)
      return []
    })
}

export const loadLendersPositions = () => async (dispatch: Dispatch) => {
  try {
    const [allPositions] = await Promise.all([getAllPositions()])
    return dispatch(getLendersPositions(allPositions))
  } catch (e) {
    console.log(e)
    return []
  } finally {
    // dispatch(stopLoadingEntities())
  }
}

export const updateLendersPosition =
  (savedLendersPosition: LendersPosition) => async (dispatch: Dispatch) => {
    const apiServerUrl = config.backendUrl

    return client
      .put(`${apiServerUrl}/api/lenders-positions`, savedLendersPosition)
      .then(response => {
        dispatch(editLendersPosition(response.data))
        return { success: true, payload: response.data }
      })
      .catch(error => {
        console.log(error)
        return { success: false, payload: error.response.data.error }
      })
  }

export const changeAllLendersForFacility =
  (lenderPositions: ChangeLender[], facilityId: StableKey, update?: boolean) =>
  async (dispatch: Dispatch) => {
    const apiServerUrl = config.backendUrl
    return client
      .post(`${apiServerUrl}/api/lenders-positions/facility`, {
        lendersPositions: lenderPositions.map(lendersPosition => ({
          amount: lendersPosition.amount,
          lender: lendersPosition.lender,
          id: lendersPosition.id,
        })),
        facilityId: facilityId,
      })
      .then((response: any) => {
        if (update != false) {
          return dispatch(
            changeLendersForFacility(
              response.data.map((lp: any) => ({ ...lp })),
            ),
          )
        } else {
          return response.data.map((lp: any) => ({ ...lp }))
        }
      })
      .catch(e => {
        return { success: false, payload: e.response.data.error }
      })
  }

export const changeAllLendersForLoan =
  (lendersPoisiotns: ChangeLender[], loanId: StableKey) =>
  async (dispatch: Dispatch) => {
    const apiServerUrl = config.backendUrl
    return client
      .post(`${apiServerUrl}/api/lenders-positions/loan`, {
        lendersPositions: lendersPoisiotns.map(lendersPosition => ({
          amount: lendersPosition.amount,
          lender: lendersPosition.lender,
          id: lendersPosition.id,
        })),
        loanId: loanId,
      })
      .then((response: any) => {
        return dispatch(
          changeLendersForLoan(response.data.map((lp: any) => ({ ...lp }))),
        )
      })
      .catch(e => {
        return { success: false, payload: e.response.data.error }
      })
  }

/**
 * A lender's facility positions; with `facilityIds`, only those on the given
 * facilities. The seller-position hover card passes the trade's facilities:
 * a large lender has ~1,900 positions (2 MB), of which it shows two or three.
 */
export const positionsForLender = async (entityId: string, facilityIds: string[] = []) => {
  const apiServerUrl = config.backendUrl
  const onFacilities = facilityIds.length > 0
    ? `&facilityIds=${encodeURIComponent(facilityIds.join(','))}`
    : ''
  return client
    .get(
      `${apiServerUrl}/api/lenders-positions/entity?id=${entityId}&type=Facilities${onFacilities}`,
    )
    .then(lendersPositions => {
      return lendersPositions.data
    })
    .catch(e => {
      console.log(e)
      return []
    })
}

export const loadMultipleLendersPositionsByFacility =
  (facilities: FacilityParams[]) => async (dispatch: Dispatch) => {
    const apiServerUrl = config.backendUrl
    dispatch(startLoadingFacilityLendersPositions())
    const results = await Promise.all(
      facilities.map(async facility => {
        try {
          const res = await client.get(
            `${apiServerUrl}/api/lenders-positions/facility?id=${facility.id}`,
          )
          const enriched = res.data.map((lp: any) => ({
            ...lp,
            status: 'Approved',
          }))
          return { facilityId: facility.id, data: enriched }
        } catch (e) {
          console.error(
            `Failed to load lender positions for facility ${facility.id}`,
            e,
          )
          return { facilityId: facility.id, data: [] }
        } finally {
          // Stop loading
          dispatch(stopLoadingFacilityLendersPositions())
        }
      }),
    )
    const grouped: Record<string, any[]> = {}
    results.forEach(({ facilityId, data }) => {
      if (facilityId) {
        grouped[facilityId] = data
      }
    })
    dispatch(getMultipleLendersPositionsByFacility(grouped))
  }

export const loadLendersPositionsByFacility = coalesceThunkByKey(
  (id: string) => async (dispatch: Dispatch) => {
    const apiServerUrl = config.backendUrl
    dispatch(startLoadingFacilityLendersPositions())
    return client
      .get(`${apiServerUrl}/api/lenders-positions/facility?id=${id}`)
      .then(lendersPositions => {
        return dispatch(
          getLendersPositionsByFacility(
            lendersPositions.data.map((lp: any) => ({
              ...lp,
              status: 'Approved',
            })),
          ),
        )
      })
      .catch(e => {
        dispatch(stopLoadingFacilityLendersPositions())
        console.log(e)
        return []
      })
  },
)

export const loadLendersPositionsByLoan = coalesceThunkByKey(
  (id: string) => async (dispatch: Dispatch) => {
    dispatch(startLoadingLoanLendersPositions())
    const apiServerUrl = config.backendUrl
    return client
      .get(`${apiServerUrl}/api/lenders-positions/loan?id=${id}`)
      .then(lendersPositions => {
        return dispatch(
          getLendersPositionsByLoan(
            lendersPositions.data.map((lp: any) => ({ ...lp })),
          ),
        )
      })
      .catch(e => {
        dispatch(stopLoadingLoanLendersPositions())
        console.log(e)
        return []
      })
  },
)

export const getAmount = (
  lendersPosition: LendersPosition,
  effectiveDate?: Date | null | string,
): number => {
  if (!lendersPosition.proRatas || lendersPosition.proRatas.length == 0) {
    return 0.0
  }
  if (typeof effectiveDate === 'string') {
    effectiveDate = stringToDate(effectiveDate)
  }
  const date = effectiveDate ?? getBusinessDateFromState()
  const proRata = lendersPosition.proRatas
    .filter(pr => {
      const prDate = stringToDate(pr.effectiveDate)
      return prDate <= date
    })
    .pop()
  return proRata ? Number(Number(proRata.amount).toFixed(2)) : 0.0
}

export const getPercentage = (
  lendersPosition: LendersPosition,
  effectiveDate?: Date | null,
): number => {
  if (!lendersPosition.status && lendersPosition.amount) {
    return Number(lendersPosition.amount)
  }
  if (!lendersPosition.proRatas || lendersPosition.proRatas.length == 0) {
    return 0.0
  }

  if (typeof effectiveDate === 'string') {
    effectiveDate = stringToDate(effectiveDate)
  }
  const date = effectiveDate ?? getBusinessDateFromState()
  const proRata = lendersPosition.proRatas
    .filter(pr => {
      const prDate = stringToDate(pr.effectiveDate)
      return prDate <= date
    })
    .pop()
  return proRata ? Number(proRata.percentage) : 0.0
}

export const getAccruedAndPaidInterest = (
  loanId: string,
  lenderId: string,
  effectiveDate: string,
) => {
  const apiServerUrl = config.backendUrl

  return client
    .get(
      `${apiServerUrl}/api/lenders-positions/interest?loanId=${loanId}&lenderId=${lenderId}&effectiveDate=${effectiveDate}`,
    )
    .then(response => {
      return { success: true, payload: response.data }
    })
    .catch(e => {
      console.log(e, 'error')
      return { success: false, payload: e.response.data.error }
    })
}
